import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Dices, RefreshCw, Loader2, RotateCcw } from 'lucide-react';
import { useAdminWheelData } from '@/hooks/useAdminWheelData';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';
import WheelStatsCards from './WheelStatsCards';
import WheelPrizeConfigEditor from './WheelPrizeConfigEditor';
import WheelSpinHistory from './WheelSpinHistory';

const WheelAdminOverview: React.FC = () => {
  const {
    loading,
    stats,
    prizes,
    spinHistory,
    savingPrizes,
    updatePrizeProbabilities,
    refreshData,
  } = useAdminWheelData();

  const [refreshing, setRefreshing] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  const handleResetAllSpins = async () => {
    const confirmMessage = 
      'Tem certeza? Isso irá:\n\n' +
      '• Permitir que TODOS os usuários girem novamente hoje\n' +
      '• APAGAR todo o histórico de giros\n' +
      '• Garantir prêmio no próximo giro de cada usuário\n\n' +
      'Esta ação não pode ser desfeita.';
    
    if (!confirm(confirmMessage)) {
      return;
    }
    
    setResetting(true);
    try {
      const { data, error } = await supabase.rpc('reset_all_daily_spins');
      
      if (error) throw error;
      
      const result = data as { success?: boolean; message?: string; error?: string } | null;
      
      if (result?.success) {
        toast.success(result.message || 'Giros resetados com sucesso!');
        await refreshData();
      } else {
        toast.error(result?.error || 'Erro ao resetar giros');
      }
    } catch (error) {
      toast.error('Erro ao resetar giros');
      debugLogger.error('AdminWheel', 'Error resetting spins:', error);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-luxury-pinkBright/30 border border-luxury-pinkBright/30">
              <Dices className="w-6 h-6 text-luxury-pinkBright" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                Gerenciamento da Roleta
              </h1>
              <p className="text-sm text-muted-foreground">
                Configure probabilidades e acompanhe estatísticas
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-white/30 bg-transparent text-white hover:bg-white/10"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Atualizar
            </Button>
            
            <Button
              size="sm"
              onClick={handleResetAllSpins}
              disabled={resetting}
              className="bg-luxury-pinkBright hover:bg-luxury-pinkBright/80 text-white"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Resetar Todos os Giros
            </Button>
          </div>
        </div>

        {/* Stats Section */}
        <WheelStatsCards stats={stats} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Prize Config Editor */}
          <WheelPrizeConfigEditor
            prizes={prizes}
            onSave={updatePrizeProbabilities}
            saving={savingPrizes}
          />

          {/* Spin History */}
          <WheelSpinHistory spins={spinHistory} />
        </div>
      </div>
    </div>
  );
};

export default WheelAdminOverview;
